import { useConfirmation } from '@components/modal';
import { useNotifications } from '@notifications/Notifications';
import {
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useToggle } from 'react-use';
import { BehaviorSubject } from 'rxjs';
import { CanvasContext } from './CanvasProvider';
import { GraphDataContext } from './GraphDataProvider';
import { PositionContext } from './PositionProvider';

interface SelectedPortType {
  nodeId: string;
  portId: string;
}

interface SelectionContextType {
  selectNode: (nodeId: string, event: MouseEvent) => void;
  selectNodesFromArea: (boxStart: [number, number], boxEnd: [number, number]) => void;
  selectLink: (linkId: string) => void;
  isSelected: (nodeId: string) => boolean;
  isSelectedLink: (linkId: string) => boolean;
  toggleInformationDrawer: () => void;
  informationDrawerOpen: boolean;
  // Kept as BehaviorSubject because Link/EditLink/OutputPort consume it via .pipe for
  // d3-bound rendering (same pattern as mousePosition$).
  selectedOutputPort?: BehaviorSubject<SelectedPortType | undefined>;
  selectedNodeList: Set<string>;
  activeNodeInformationNode: string;
  setActiveNodeInformationNode: Dispatch<SetStateAction<string>>;
}

interface SelectionProviderProps {
  disabled?: boolean;
}

export const SelectionContext = createContext({} as SelectionContextType);

export const SelectionProvider = ({
  children,
  disabled,
}: PropsWithChildren<SelectionProviderProps>) => {
  const { svg, isEditingMode$ } = useContext(CanvasContext);
  const { getNodeListInBound } = useContext(PositionContext);
  const { duplicateNodes, removeSelectedNode } = useContext(GraphDataContext);
  const { showConfirmation } = useConfirmation();
  const { addNotification } = useNotifications();

  const [selectedNodeList, setSelectedNodeList] = useState<Set<string>>(new Set());
  const [selectedLink, setSelectedLink] = useState<string | undefined>(undefined);
  const [copiedNodeList, setCopiedNodeList] = useState<Set<string>>(new Set());
  const [activeNodeInformationNode, setActiveNodeInformationNode] = useState<string>(null);
  const [informationDrawerOpen, toggleInformationDrawer] = useToggle(false);

  // Kept as BehaviorSubject — consumed via .pipe in Link/EditLink/OutputPort for d3 rendering.
  const [selectedOutputPort] = useState<BehaviorSubject<SelectedPortType | undefined>>(
    new BehaviorSubject(undefined),
  );

  // Refs mirror state so callbacks invoked inside d3 event handlers (or async flows)
  // always read current values rather than a stale closure.
  const selectedNodeListRef = useRef(selectedNodeList);
  selectedNodeListRef.current = selectedNodeList;
  const copiedNodeListRef = useRef(copiedNodeList);
  copiedNodeListRef.current = copiedNodeList;
  const selectedLinkRef = useRef(selectedLink);
  selectedLinkRef.current = selectedLink;

  const clearSelection = useCallback(() => {
    setSelectedNodeList(new Set());
    selectedOutputPort.next(undefined);
    setSelectedLink(undefined);
  }, [selectedOutputPort]);

  const deleteSelectedNodes = useCallback(() => {
    const selectedNodes = Array.from(selectedNodeListRef.current);
    if (isEditingMode$.getValue() || disabled || selectedNodes.length < 1) {
      return;
    }

    void showConfirmation({
      onAffirm: () => {
        selectedNodes.forEach((nodeId, index) =>
          removeSelectedNode(nodeId, index === selectedNodes.length - 1),
        );
        addNotification({ text: 'Deleted node(s)', type: 'success' });
      },
      message: 'Are you sure you want to delete the selected node(s)?',
    });

    clearSelection();
  }, [
    isEditingMode$,
    disabled,
    showConfirmation,
    removeSelectedNode,
    addNotification,
    clearSelection,
  ]);

  const copy = useCallback(() => {
    if (isEditingMode$.getValue()) return;
    const current = selectedNodeListRef.current;
    if (!current?.size) return;
    setCopiedNodeList(current);
    addNotification({ type: 'info', text: 'Copied selected nodes' });
  }, [isEditingMode$, addNotification]);

  const paste = useCallback(() => {
    if (isEditingMode$.getValue()) return;
    const copied = copiedNodeListRef.current;
    if (!copied?.size) return;
    duplicateNodes(Array.from(copied));
  }, [isEditingMode$, duplicateNodes]);

  useEffect(() => {
    svg
      ?.on('click', (event: MouseEvent) => {
        if (!event.shiftKey && event.target === svg.node()) {
          clearSelection();
        }
      })
      .on('keyup', (event: KeyboardEvent & { target: { localName: string } }) => {
        if (
          (event.key === 'Delete' || event.key === 'Backspace') &&
          event.target.localName === 'svg'
        ) {
          deleteSelectedNodes();
        } else if (event.key === 'Escape') {
          clearSelection();
        } else if (event.ctrlKey && event.key === 'c') {
          copy();
        } else if (event.ctrlKey && event.key === 'v') {
          void paste();
        }
      });
  }, [svg, clearSelection, deleteSelectedNodes, copy, paste]);

  const selectNode = useCallback((nodeId: string, event: MouseEvent) => {
    if (!event.shiftKey) {
      setSelectedNodeList(new Set([nodeId]));
      setSelectedLink(undefined);
    } else {
      setSelectedNodeList((prev) => {
        const next = new Set(prev);
        next.add(nodeId);
        return next;
      });
    }
  }, []);

  const selectNodesFromArea = useCallback(
    (boxStart: [number, number], boxEnd: [number, number]) => {
      setSelectedNodeList(new Set(getNodeListInBound(boxStart, boxEnd)));
      setSelectedLink(undefined);
    },
    [getNodeListInBound],
  );

  const selectLinkFn = useCallback((linkId: string) => {
    setSelectedNodeList(new Set());
    setSelectedLink(linkId);
  }, []);

  const isSelected = useCallback((nodeId: string) => {
    return selectedNodeListRef.current.has(nodeId);
  }, []);

  const isSelectedLink = useCallback((linkId: string) => {
    return linkId === selectedLinkRef.current;
  }, []);

  const providerValue = useMemo(
    () => ({
      selectNode,
      selectNodesFromArea,
      selectLink: selectLinkFn,
      isSelected,
      isSelectedLink,
      toggleInformationDrawer,
      selectedNodeList,
      selectedOutputPort,
      activeNodeInformationNode,
      setActiveNodeInformationNode,
      informationDrawerOpen,
    }),
    [
      selectNode,
      selectNodesFromArea,
      selectLinkFn,
      isSelected,
      isSelectedLink,
      toggleInformationDrawer,
      selectedNodeList,
      selectedOutputPort,
      activeNodeInformationNode,
      informationDrawerOpen,
    ],
  );

  return <SelectionContext.Provider value={providerValue}>{children}</SelectionContext.Provider>;
};
